import React from "react";
import { formatFileSize, getFileIcon } from "../../../../libs/utils";
import DownloadProgress from "./DownloadProgress"; // Assuming DownloadProgress is in the same folder

const FileCard = ({
  file,
  workspace,
  fileId,
  theme,
  downloadingFile,
  downloadingFiles,
  isMobile,
  handlePreviewFile,
  handleDownloadFiles,
  handleDeleteClick,
  loadingOverwrite,
  deletingFile,
  progress,
  showProgress,
  downloadInfo,
  formatBytes,
}) => {
  const file_extension = file?.name.split(".").pop();

  const isViewableFile = (file_extension) => {
    // Define file types that are allowed for preview (images, PDF, MP4)
    const viewableExtensions = [
      "pdf",
      "jpg",
      "jpeg",
      "png",
      "gif",
      "bmp",
      "ico",
      "svg",
      "mp4",
    ];
    return viewableExtensions.includes(file_extension.toLowerCase());
  };

  return (
    <div key={fileId}>
      <div
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: theme === "dark-only" ? "#1d1e26" : "#fff",
          boxShadow: showProgress
            ? theme === "dark-only"
              ? "0 -2px 6px rgba(255,255,255,0.1)" // Top shadow only, bottom removed
              : "0 -5px 6px rgba(0,0,0,0.15)" // Top shadow only, bottom removed
            : theme === "dark-only"
            ? "0 2px 6px rgba(255,255,255,0.1)" // Normal shadow
            : "0 2px 6px rgba(0,0,0,0.15)", // Normal shadow
          borderRadius: "8px 8px 0 0", // Rounded only on top when progress is shown
          borderBottomLeftRadius: showProgress ? 0 : 8,
          borderBottomRightRadius: showProgress ? 0 : 8,
          padding: "10px 16px",
          marginBottom: showProgress ? 0 : 10,
          color: theme === "dark-only" ? "#f1f1f1" : "#000",
          flexWrap: "wrap",
          minHeight: "48px",
        }}
      >
        {/* File Icon */}
        <div
          className="flex flex-col justify-center items-center bg-gray-100 text-gray-500"
          style={{
            width: 48,
            height: 48,
            marginRight: 12,
            borderRadius: 6,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div className="w-full flex justify-center">
            {getFileIcon(file_extension)}{" "}
            {/* Assuming getFileIcon is a function to get the file icon */}
          </div>
        </div>

        {/* File Details (Centered) */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column", // Adjusted to stack file name and size vertically
            alignItems: "flex-start",
            minWidth: 0,
            wordBreak: "break-word",
            whiteSpace: "normal",
          }}
        >
          <p style={{ fontSize: 12, margin: 0 }}>{file.name}</p>
          <p style={{ fontSize: 10, margin: 0, color: "#777" }}>
            {formatFileSize(file?.fileInfo[0]?.fileSize)}
            {/* {formatBytes(file?.fileInfo[0]?.fileSize)} */}
          </p>
        </div>

        {/* Preview Button */}
        <button
          onClick={() =>
            handlePreviewFile(fileId, file?.fileInfo[0], file.workspace)
          }
          style={{
            background: downloadingFiles === fileId ? "#001F5B" : "#005A9C",
            color: "#fff",
            border: "none",
            borderRadius: 6,
            padding: "6px 12px",
            cursor:
              downloadingFiles === fileId || !isViewableFile(file_extension)
                ? "not-allowed"
                : "pointer",
            fontSize: "14px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "background 0.2s",
            opacity:
              downloadingFiles === fileId || !isViewableFile(file_extension)
                ? 0.7
                : 1,
            height: "36px",
            marginRight: 8,
          }}
          disabled={
            downloadingFile === fileId || !isViewableFile(file_extension)
          }
        >
          {downloadingFiles === fileId ? (
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <i className="fa fa-spinner fa-spin" style={{ fontSize: 14 }}></i>
              {!isMobile && "Loading..."}
            </span>
          ) : (
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <i className="fa fa-eye" style={{ fontSize: 14 }}></i>
              {!isMobile && "View"}
            </span>
          )}
        </button>

        {/* Download Button */}
        <button
          onClick={() =>
            handleDownloadFiles(
              file,
              [
                {
                  name: file.name,
                  path: fileId,
                },
              ],
              workspace
            )
          }
          style={{
            background: downloadingFile === fileId ? "#001F5B" : "#002878",
            color: "#fff",
            border: "none",
            borderRadius: 6,
            padding: "6px 12px",
            cursor: downloadingFile === fileId ? "not-allowed" : "pointer",
            fontSize: "14px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "background 0.2s",
            opacity: downloadingFile === fileId ? 0.7 : 1,
            height: "36px",
          }}
          disabled={downloadingFile === fileId}
        >
          {downloadingFile === fileId ? (
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <i className="fa fa-spinner fa-spin" style={{ fontSize: 14 }}></i>
              {!isMobile && "Downloading..."}
            </span>
          ) : (
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <i className="fa fa-download" style={{ fontSize: 14 }}></i>
              {!isMobile && "Download"}
            </span>
          )}
        </button>

        {/* Delete Button */}
        <button
          onClick={() => handleDeleteClick(file)}
          style={{
            background: "#fc4438",
            color: "#fff",
            border: "none",
            borderRadius: 6,
            padding: "6px 12px",
            marginLeft: 8,
            cursor: loadingOverwrite === fileId ? "not-allowed" : "pointer",
            fontSize: "14px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "background 0.2s",
            opacity: loadingOverwrite === fileId ? 0.7 : 1,
            height: "36px",
          }}
          disabled={loadingOverwrite === fileId}
        >
          {deletingFile === fileId ? (
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <i className="fa fa-spinner fa-spin" style={{ fontSize: 14 }}></i>
              {!isMobile && "Deleting..."}
            </span>
          ) : (
            <i
              className={
                deletingFile === fileId
                  ? "fa fa-spinner fa-spin"
                  : "fa fa-trash"
              }
              style={{ fontSize: 14 }}
            ></i>
          )}
        </button>
      </div>

      {/* Progress Bar - shown only when downloading */}
      {showProgress && (
        <DownloadProgress
          key={file.path}
          progress={progress}
          theme={theme}
          fileName={file.name}
          downloadInfo={downloadInfo}
          formatBytes={formatBytes}
        />
      )}
    </div>
  );
};

export default FileCard;
